import React, { useState, useEffect } from 'react';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import styled from 'styled-components';
import { navDelay, loaderDelay } from '@utils';
import { usePrefersReducedMotion } from '@hooks';

const StyledHeroSection = styled.section`
  ${({ theme }) => theme.mixins.flexCenter};
  flex-direction: column;
  align-items: flex-start;
  min-height: 90vh;
  /* Reserve space for the fixed nav so centered content never hides beneath it */
  padding: var(--nav-height) 0 0;

  /* On small or short viewports (phones, scaled Windows displays), don't center —
     top-align below the nav and let the section grow so nothing gets clipped. */
  @media (max-width: 768px), (max-height: 720px) {
    justify-content: flex-start;
    min-height: auto;
    padding-top: calc(var(--nav-height) + 40px);
    padding-bottom: 60px;
  }

  h1 {
    margin: 0 0 30px 4px;
    color: var(--green);
    font-family: var(--font-mono);
    font-size: clamp(var(--fz-sm), 5vw, var(--fz-md));
    font-weight: 400;

    @media (max-width: 480px) {
      margin: 0 0 20px 2px;
    }
  }

  h3 {
    margin-top: 5px;
    color: var(--slate);
    line-height: 0.9;
  }

  p {
    margin: 20px 0 0;
    max-width: 540px;
  }

  .email-link {
    ${({ theme }) => theme.mixins.bigButton};
    margin-top: 50px;
  }
`;

const Hero = () => {
  const [isMounted, setIsMounted] = useState(false);
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    if (prefersReducedMotion) {
      return;
    }

    const timeout = setTimeout(() => setIsMounted(true), navDelay);
    return () => clearTimeout(timeout);
  }, []);

  const one = <h1>Hi, my name is</h1>;
  const two = <h2 className="big-heading">Tejeshwar Gill.</h2>;
  const three = (
    <h3 className="big-heading">I build and scale AI-driven engineering organizations.</h3>
  );
  const four = (
    <>
      <p>
        I'm an engineering leader with 17 years of experience building and scaling
        high-performance teams across Healthcare AI, Cybersecurity, and AdTech. A UCLA Anderson
        Executive MBA and BITS Pilani M.S. in Software Engineering, I operate at the intersection of
        platform strategy and cross-functional execution — turning business strategy into
        technology roadmaps that ship at scale.
      </p>
    </>
  );
  const five = (
    <a className="email-link" href="/#projects" rel="noreferrer">
      See what I've built!
    </a>
  );

  const items = [one, two, three, four, five];

  return (
    <StyledHeroSection>
      {prefersReducedMotion ? (
        <>
          {items.map((item, i) => (
            <div key={i}>{item}</div>
          ))}
        </>
      ) : (
        <TransitionGroup component={null}>
          {isMounted &&
            items.map((item, i) => (
              <CSSTransition key={i} classNames="fadeup" timeout={loaderDelay}>
                <div style={{ transitionDelay: `${i + 1}00ms` }}>{item}</div>
              </CSSTransition>
            ))}
        </TransitionGroup>
      )}
    </StyledHeroSection>
  );
};

export default Hero;
